// client/src/hooks/useDashboard.js
import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '../queryClient'
import ApiDashboard from '../api/ApiDashboard'
import useAuth from './useAuth'

/**
 * Unified dashboard hook - replaces multiple individual hooks
 * Fetches all dashboard data in a single API call
 *
 * Replaces:
 * - useDashboardData (for leagues)
 * - useNextMatch
 * - useRecentAwards
 * - useOverdueMatchesCount
 * - getReceivedProposals (from useScheduledMatches)
 */
export function useDashboard() {
  const { user, loading: authLoading } = useAuth()

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.dashboardSummary(),
    queryFn: async () => {
      const response = await ApiDashboard.getSummary()

      if (!response.success) {
        throw new Error(response.error || 'Failed to load dashboard data')
      }

      return response.data
    },
    enabled: !authLoading && !!user,
    staleTime: 1 * 60 * 1000, // 1 minute - dashboard data changes frequently
  })

  // Extract individual data pieces from the response
  const leagues = data?.leagues || []
  const nextMatch = data?.nextMatch || null
  const recentAwards = data?.recentAwards || []
  const overdueMatchesCount = data?.overdueMatchesCount || 0
  const proposals = data?.proposals || []
  const scheduledMatchesCount = data?.scheduledMatchesCount || 0
  const matchesPlayedCount = data?.matchesPlayedCount || 0

  return {
    // Individual data pieces
    leagues,
    nextMatch,
    recentAwards,
    overdueMatchesCount,
    proposals,
    scheduledMatchesCount,
    matchesPlayedCount,

    // Derived data for backwards compatibility
    leagueCount: leagues.length,

    // Loading and error states
    loading: authLoading || isLoading,
    error: error?.message || null,

    // Refetch function
    refetch,
  }
}

export default useDashboard
